import { useQuery } from "@tanstack/react-query";
import { cardIssuanceStatusQueryConfig, TPaymentCardResponse } from "../../../../../api";
import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { RoutesMap } from "../../../../../routes.tsx";
import { isPendingCardStatus } from "./utils";

export function useCardIssuanceStatusReaction(limitId: string | undefined, enabled: boolean) {
  const navigate = useNavigate();
  const lastHandledRef = useRef<string | null>(null);

  const cardIssuanceStatus = useQuery({
    ...cardIssuanceStatusQueryConfig(limitId ?? ""),
    enabled: enabled && !!limitId,
    refetchInterval: (query) => {
      const { data } = query.state;
      if (isPendingCardStatus(data)) {
        return 10000; // 10 seconds
      }
      return false;
    },
    refetchIntervalInBackground: true,
  });

  const handleFinalStatus = useCallback(
    (status: TPaymentCardResponse) => {
      if (status === "ERROR") {
        // ErrorPage will be shown by parent component
        return;
      }

      if (status === "OTP_EXPECTED") {
        navigate(RoutesMap.login.otpB2p);
        return;
      }
    },
    [navigate],
  );

  useEffect(() => {
    // Check HTTP response status, not card status value
    // If HTTP 200 (success) with non-pending status -> navigate to main page
    // Only process if query is enabled (enabled prop) and query succeeded
    if (enabled && cardIssuanceStatus.isSuccess) {
      const status = cardIssuanceStatus.data;

      const handledKey = `${limitId ?? ""}:${status}`;
      if (lastHandledRef.current === handledKey) {
        return;
      }
      lastHandledRef.current = handledKey;

      // Continue polling for pending statuses
      if (isPendingCardStatus(status)) {
        return;
      }

      // Handle ERROR and OTP_EXPECTED statuses - these return early
      if (status === "ERROR" || status === "OTP_EXPECTED") {
        handleFinalStatus(status);
        return;
      }

      // HTTP 200 (success) -> navigate to main page for other statuses
      navigate(RoutesMap.index);
    }
  }, [enabled, limitId, cardIssuanceStatus.isSuccess, cardIssuanceStatus.data, handleFinalStatus, navigate]);

  return cardIssuanceStatus;
}
